import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, useTheme } from "@mui/material";

/** @param {{ fullText?: string }} props */
const ReasoningSnippet = ({ fullText }) => {
  const theme = useTheme();
  const tc = theme.customColors;
  const [visibleCount, setVisibleCount] = useState(0);
  const [done, setDone] = useState(false);
  const wordsRef = useRef([]);

  useEffect(() => {
    const source = (fullText || "").trim();
    if (!source) {
      wordsRef.current = [];
      setVisibleCount(0);
      setDone(true);
      return;
    }

    const words = source.split(/(\s+)/);
    wordsRef.current = words;
    setVisibleCount(0);
    setDone(false);

    const realWords = words.filter((w) => w.trim()).length;
    const basePace = Math.min(50, Math.max(15, 1400 / realWords));
    let idx = 0;
    let lastTs = performance.now();

    const step = () => {
      const now = performance.now();
      const chunk = Math.max(1, Math.floor((now - lastTs) / basePace));
      idx = Math.min(words.length, idx + chunk);
      lastTs = now;
      setVisibleCount(idx);
      if (idx >= words.length) {
        setDone(true);
        return;
      }
      requestAnimationFrame(step);
    };

    const raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [fullText]);

  const words = wordsRef.current;

  return (
    <Box
      sx={{
        mb: 2,
        "@keyframes geminiReveal": {
          "0%": { opacity: 0, filter: "blur(4px)", color: "#3B82F6" },
          "40%": { opacity: 0.7, filter: "blur(1px)", color: "#60A5FA" },
          "100%": { opacity: 1, filter: "blur(0px)", color: tc.text.secondary },
        },
      }}
    >
      <Typography
        variant="body2"
        component="div"
        sx={{
          fontSize: "0.8125rem",
          color: tc.text.secondary,
          lineHeight: 1.65,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {words.slice(0, visibleCount).map((word, i) => {
          const isRecent = !done && i >= visibleCount - 6;
          return (
            <span
              key={i}
              style={
                isRecent
                  ? {
                      animation: "geminiReveal 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards",
                    }
                  : undefined
              }
            >
              {word}
            </span>
          );
        })}
      </Typography>
    </Box>
  );
};

export default ReasoningSnippet;
